import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpLeft } from "lucide-react";
import { TinyStar } from "./Stickers";

const sizeClasses = {
  lg: "",
  wide: "",
  tall: "",
  sm: "",
};

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.92, rotate: -1.5 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function BentoCard({ project, index = 0 }) {
  const span = project.gridClass || sizeClasses[project.size] || "";
  const isAccent = project.variant === "accent";
  const isType = project.variant === "type";
  const isImage = project.variant === "image";

  return (
    <motion.div
      variants={cardVariants}
      className={`${span} min-h-[240px] md:min-h-0`}
    >
      <Link
        to={`/projects/${project.slug}`}
        data-testid={`bento-card-${project.slug}`}
        className={`bento-card ${
          isAccent ? "accent-card" : ""
        } relative block h-full w-full overflow-hidden rounded-2xl ink-border group ${
          isAccent
            ? "bg-[color:var(--pink)] text-[color:var(--paper)]"
            : isType
              ? "bg-[color:var(--paper)]"
              : "bg-[color:var(--warm)]"
        }`}
      >
        {/* Image variant */}
        {isImage && project.cover && (
          <div className="absolute inset-0">
            <img
              src={project.cover}
              alt={project.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
          </div>
        )}

        {/* Type variant - big typographic cover */}
        {isType && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display font-black text-[18vw] md:text-[7vw] leading-none text-[color:var(--ink)] opacity-10 select-none">
              {project.title.slice(0, 2)}
            </span>
          </div>
        )}

        {/* Content overlay */}
        <div
          className={`relative z-10 h-full w-full p-5 md:p-7 flex flex-col justify-between ${
            isImage ? "text-[color:var(--paper)]" : ""
          }`}
        >
          <div className="flex items-start justify-between">
            <span
              className={`text-xs font-medium tracking-wide ${
                isImage
                  ? "bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full"
                  : isAccent
                    ? "bg-black/20 px-3 py-1 rounded-full"
                    : "tag-pill"
              }`}
            >
              {project.category}
            </span>
            <ArrowUpLeft
              className={`w-7 h-7 transition-all duration-300 group-hover:rotate-[-12deg] group-hover:scale-110 ${
                isImage || isAccent ? "" : "text-[color:var(--ink)]"
              }`}
              strokeWidth={1.5}
            />
          </div>

          <div className="space-y-2">
            <h3
              className={`font-display font-black tracking-tight leading-[0.9] ${
                project.size === "lg"
                  ? "text-4xl md:text-6xl"
                  : project.size === "wide" || project.size === "tall"
                    ? "text-3xl md:text-5xl"
                    : "text-2xl md:text-3xl"
              }`}
            >
              {project.title}
            </h3>
            <div className="flex items-center gap-2 opacity-80">
              <TinyStar
                className="w-3 h-3"
                color={isImage || isAccent ? "#FDFCF8" : "#FF1493"}
              />
              <span className="text-sm font-medium">{project.role}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
